import { MenuIcon, PanelLeftCloseIcon, PanelLeftOpenIcon } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../shared/lib/cn';
import { Button } from '../../shared/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../shared/ui/sheet';
import { AccountMenu } from './account-menu';
import type { NavItem, ShellSlot } from './nav-config';
import { SidebarContent } from './sidebar';
import { TopBarContext, type TopBarSlots } from './top-bar';
import { useCollapsed } from './use-collapsed';

type AppShellProps = {
  userEmail: string;
  userName?: { firstName: string; lastName: string };
  /** Primary navigation: the visible workspaces of the active org, or the settings sections. */
  navItems: NavItem[];
  navLabel: string;
  /** Offer a search field over `navItems`, with this label. */
  searchLabel?: string;
  /** Above the navigation heading: the way back out of settings mode. */
  backLink?: { label: string; to: string };
  navFooterSlot?: ShellSlot;
  /** At the foot of the sidebar: Settings, Staff area, the design reference in dev builds. */
  bottomItems?: NavItem[];
  /** The organization switcher, under the bottom items. It must work at both 240px and 64px. */
  switcherSlot?: ShellSlot;
  /** A standing marker at the start of the top bar (the staff area says "Staff" here, so it is never mistaken for a customer view). */
  topBarMarker?: ReactNode;
  children: ReactNode;
};

/**
 * The signed-in frame: navy sidebar (collapsible to an icon rail, a drawer below `md`) and a slim
 * top bar that pages fill through `TopBarBreadcrumb` / `TopBarActions`.
 */
export function AppShell({
  userEmail,
  userName,
  navItems: items,
  navLabel,
  searchLabel,
  backLink,
  navFooterSlot,
  bottomItems,
  switcherSlot,
  topBarMarker,
  children,
}: AppShellProps) {
  const { t } = useTranslation('shell');
  const [collapsed, toggleCollapsed] = useCollapsed();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState<HTMLElement | null>(null);
  const [actions, setActions] = useState<HTMLElement | null>(null);
  const slots = useMemo<TopBarSlots>(() => ({ breadcrumb, actions }), [breadcrumb, actions]);
  const sidebar = {
    items,
    navLabel,
    ...(searchLabel ? { searchLabel } : {}),
    ...(backLink ? { backLink } : {}),
    ...(navFooterSlot ? { navFooterSlot } : {}),
    ...(bottomItems ? { bottomItems } : {}),
    ...(switcherSlot ? { switcherSlot } : {}),
  };

  return (
    <div className="flex min-h-dvh">
      <a
        href="#main"
        className="sr-only z-50 rounded-full bg-card px-4 py-2 font-bold focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        {t('skipToContent')}
      </a>

      <aside
        aria-label={t('sidebar')}
        data-collapsed={collapsed}
        className={cn(
          'sticky top-0 hidden h-dvh shrink-0 overflow-x-hidden overflow-y-auto bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-standard md:block',
          collapsed ? 'w-16' : 'w-60',
        )}
      >
        <SidebarContent {...sidebar} collapsed={collapsed} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b bg-card px-3 sm:px-4">
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label={t('openMenu')}>
                <MenuIcon aria-hidden="true" className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-72 gap-0 border-sidebar-border bg-sidebar p-0 text-sidebar-foreground [&_[data-slot=sheet-close-button]]:text-sidebar-foreground [&_[data-slot=sheet-close-button]]:outline-sidebar-ring [&_[data-slot=sheet-close-button]]:hover:bg-sidebar-hover"
            >
              <SheetHeader className="sr-only">
                <SheetTitle>{t('menu')}</SheetTitle>
                <SheetDescription>{t('menuDescription')}</SheetDescription>
              </SheetHeader>
              <SidebarContent
                {...sidebar}
                collapsed={false}
                onNavigate={() => setDrawerOpen(false)}
              />
            </SheetContent>
          </Sheet>
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:inline-flex"
            aria-label={collapsed ? t('expand') : t('collapse')}
            aria-expanded={!collapsed}
            onClick={toggleCollapsed}
          >
            {collapsed ? (
              <PanelLeftOpenIcon aria-hidden="true" className="size-5" />
            ) : (
              <PanelLeftCloseIcon aria-hidden="true" className="size-5" />
            )}
          </Button>
          {topBarMarker}
          <div ref={setBreadcrumb} data-slot="top-bar-breadcrumb" className="min-w-0 flex-1" />
          <div
            ref={setActions}
            data-slot="top-bar-actions"
            className="flex shrink-0 items-center gap-2"
          />
          <AccountMenu userEmail={userEmail} {...(userName ? { userName } : {})} />
        </header>

        <TopBarContext.Provider value={slots}>
          <main id="main" tabIndex={-1} className="flex min-h-0 flex-1 flex-col outline-none">
            {children}
          </main>
        </TopBarContext.Provider>
      </div>
    </div>
  );
}
